import type { ExprNode } from '@revelio/data-access';
import { PositionTextKind, UserExprTextKind } from '@revelio/data-access';

import { getFilterDefinition } from '../../engine/filterRegistry';
import type { FreeTextFilter } from '../../types/filters';
import { FilterId, FilterType } from '../../types/filters';
import { collectTxtLeaves } from './expr';
import { appendInstance, upsertInstance } from './instances';

export const transformTextLeavesInConjunct = ({
  conjunct,
  isNegated,
  groupFreeTextInstances,
  globalFreeTextInstances,
}: {
  conjunct: ExprNode;
  isNegated: boolean;
  groupFreeTextInstances: Map<FilterId, FreeTextFilter[]>;
  globalFreeTextInstances: Map<FilterId, FreeTextFilter[]>;
}): void => {
  // A "text leaf" is an ExprNode like:
  //   txt.user_attribute = { kind: Desc, text: "Machine Learning" }
  //   txt.pos_attribute  = { kind: Title, text: "Engineer", curr: true }
  const txtLeaves = collectTxtLeaves(conjunct, isNegated);
  if (txtLeaves.length === 0) return;

  // KEYWORDS (UserExprTextKind.Desc)
  // Example:
  // - `(desc="ML" OR desc="DL")` → one KEYWORDS instance with text ["ML", "DL"]
  // - `NOT desc="NLP"` → a separate KEYWORDS instance with isNot=true
  const keywordTexts = txtLeaves
    .filter((l) => l.txt.user_attribute?.kind === UserExprTextKind.Desc)
    .map((l) => ({
      text: l.txt.user_attribute?.text ?? '',
      isNot: l.isNegated,
    }))
    .filter((t) => t.text.trim().length > 0);

  if (keywordTexts.length > 0) {
    const definition = getFilterDefinition(FilterId.KEYWORDS);
    if (definition && definition.type === FilterType.FREE_TEXT) {
      const yes = keywordTexts
        .filter((t) => !t.isNot)
        .map((t) => t.text.trim());
      const not = keywordTexts.filter((t) => t.isNot).map((t) => t.text.trim());

      if (yes.length > 0) {
        appendInstance(groupFreeTextInstances, FilterId.KEYWORDS, {
          ...(definition as FreeTextFilter),
          text: Array.from(new Set(yes)),
          isNot: false,
          isVisible: true,
          isUserSubmitted: true,
        });
      }
      if (not.length > 0) {
        appendInstance(groupFreeTextInstances, FilterId.KEYWORDS, {
          ...(definition as FreeTextFilter),
          text: Array.from(new Set(not)),
          isNot: true,
          isVisible: true,
          isUserSubmitted: true,
        });
      }
    }
  }

  // TITLE (PositionTextKind.Title)
  // Titles can be "current" or "previous", so we keep them in separate instances:
  // - `title(curr=true)="Engineer" AND title(curr=false)="Analyst"`
  //   → MULTI_TITLE with two instances (isCurrent=true vs false)
  const titleTexts = txtLeaves
    .filter((l) => l.txt.pos_attribute?.kind === PositionTextKind.Title)
    .map((l) => ({
      text: l.txt.pos_attribute?.text ?? '',
      curr: l.txt.pos_attribute?.curr ?? true,
      isNot: l.isNegated,
    }))
    .filter((t) => t.text.trim().length > 0);

  if (titleTexts.length > 0) {
    const definition = getFilterDefinition(FilterId.TITLE);
    if (definition && definition.type === FilterType.FREE_TEXT) {
      const byKey = new Map<
        string,
        { isCurrent: boolean; isNot: boolean; texts: string[] }
      >();
      titleTexts.forEach((t) => {
        const key = `${t.curr}-${t.isNot}`;
        const existing = byKey.get(key);
        if (existing) {
          existing.texts.push(t.text.trim());
        } else {
          byKey.set(key, {
            isCurrent: t.curr,
            isNot: t.isNot,
            texts: [t.text.trim()],
          });
        }
      });

      byKey.forEach((v) => {
        appendInstance(groupFreeTextInstances, FilterId.TITLE, {
          ...(definition as FreeTextFilter),
          text: Array.from(new Set(v.texts)),
          isCurrent: v.isCurrent,
          isNot: v.isNot,
          isVisible: true,
          isUserSubmitted: true,
        });
      });
    }
  }

  // NAME (UserExprTextKind.Name)
  // Name does not support multi instances; keep best-effort single filter.
  // If both positive and negated names exist, we prefer the non-negated one.
  const nameTexts = txtLeaves
    .filter((l) => l.txt.user_attribute?.kind === UserExprTextKind.Name)
    .map((l) => ({
      text: l.txt.user_attribute?.text ?? '',
      isNot: l.isNegated,
    }))
    .filter((t) => t.text.trim().length > 0);

  if (nameTexts.length > 0) {
    const definition = getFilterDefinition(FilterId.NAME);
    if (definition && definition.type === FilterType.FREE_TEXT) {
      const preferred = nameTexts.find((t) => !t.isNot) ?? nameTexts[0] ?? null;
      if (preferred) {
        upsertInstance(
          globalFreeTextInstances,
          FilterId.NAME,
          () => true,
          () => ({
            ...(definition as FreeTextFilter),
            text: [preferred.text.trim()],
            isNot: preferred.isNot,
            isVisible: true,
            isUserSubmitted: true,
          }),
          () => ({
            ...(definition as FreeTextFilter),
            text: [preferred.text.trim()],
            isNot: preferred.isNot,
            isVisible: true,
            isUserSubmitted: true,
          })
        );
      }
    }
  }
};
